import { useCallback, useMemo } from 'react';
import { mapSearchListingResultToPropertyContext, mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import {
  getABTestContext,
  getAiSearchContext,
  getConsentDocumentContext,
  getNavigationContext,
  getPageContext,
  getPropertyContext,
  getShopContext,
  trackNavigationEvent,
} from '@ev-tech/snowplow-library';
import { type SearchListingResult } from '@ev-tech/search-modules-api';

import { useAppContext } from './useAppContext';
import { useSearchStore } from './stores/search/useSearchStore';
import { SearchMode } from './stores/search/slices/searchModeSlice';
import { PageType, TemplateType } from '../types/tracking';

export const useSearchModuleTracking = () => {
  const {
    options: { language },
    countryCode,
  } = useAppContext();

  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const masterDataLiPaIds = useSearchStore((state) => state.masterDataLiPaIds);
  const businessArea = useSearchStore((state) => state.objectTypeCategory);
  const shopIds = useSearchStore((state) => state.shopIds);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);

  const { tracking } = useAppContext();

  const isLipaPage = shopIds || masterDataShopIds;

  const pageContext = useMemo(
    () =>
      getPageContext({
        type: tracking?.pageType ?? (isLipaPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
        isHQPage: true,
        template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
        page_language: language,
        country_code: countryCode,
      }),
    [tracking?.pageType, tracking?.templateType, isLipaPage, language, countryCode],
  );

  const shopContext = useMemo(
    () =>
      getShopContext({
        shop_id: masterDataShopIds?.[0],
        license_id: masterDataLiPaIds?.[0],
        business_division_name: businessArea,
      }),
    [masterDataShopIds, masterDataLiPaIds, businessArea],
  );

  // Must be a function, not a useMemo value: useMapTracking (used inside MapVisibilityStoreProvider)
  // calls this hook, so reading from useMapVisibilityStore here would create a circular dependency.
  // Callers that need the live map state must read it themselves and pass it as the argument.
  const mapABTestContext = useCallback(
    (isMapActive: boolean) =>
      getABTestContext({
        id: 'isMapToggledOn',
        name: 'isMapToggledOn',
        variant: isMapActive ? 'true' : 'false',
        variant_description: 'map initially toggled on or off',
      }),
    [],
  );

  const trackSearchResultCardClick = useCallback(
    ({
      exposeUrl,
      listingResult,
      elementId,
      isMapActive,
    }: {
      exposeUrl: string;
      listingResult: SearchListingResult;
      elementId: string;
      isMapActive: boolean;
    }) => {
      const propertyContext = mapSearchListingResultToPropertyContext(listingResult);

      trackNavigationEvent({
        screenName: '',
        contexts: [
          pageContext,
          shopContext,
          getConsentContext(),
          getPropertyContext(propertyContext),
          getNavigationContext({
            target_url: exposeUrl,
            element_id: elementId,
          }),
          mapABTestContext(isMapActive),
          getAiSearchContext(
            mapSearchStateToAiSearchContext({
              preferences,
              isAiSearchEnabled: searchMode === SearchMode.AI,
              preferenceScoreOverall: listingResult.listing?.preferenceScoreOverall,
            }),
          ),
        ],
      });
    },
    [pageContext, shopContext, mapABTestContext, preferences, searchMode],
  );

  return {
    mapABTestContext,
    pageContext,
    shopContext,
    trackSearchResultCardClick,
  };
};

export const getConsentContext = () => {
  return getConsentDocumentContext({
    vendors_consent: Object.values(window.Didomi?.getCurrentUserStatus().vendors ?? {})
      .filter((vendor) => vendor.enabled)
      .map((vendor) => vendor.id)
      .join(','),
    vendors_consent_denied: Object.values(window.Didomi?.getCurrentUserStatus().vendors ?? {})
      .filter((vendor) => !vendor.enabled)
      .map((vendor) => vendor.id)
      .join(','),
  });
};
